import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column } from "@/components/Column";
import { KeyboardShortcut } from "@/components/KeyboardShortcut";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { FormDateInput } from "@/components/form/FormDateInput";
import { FormTextAreaInput } from "@/components/form/FormTextAreaInput";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

import {
  candidacyRejectionReasonGroupToNotePlaceholder,
  candidacyRejectionReasonGroupToReasons,
  candidacyRejectionReasonToPlacementStatus,
  candidacyRejectionReasonToSubtitle,
  candidacyRejectionReasonToTitle,
} from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { nullableDateSchema } from "@/lib/schemas";
import { cn } from "@/lib/utils";
import {
  CandidacyRejectionReason,
  CandidacyRejectionReasonGroup,
  PlacementStatus,
} from "@/types";

// Digits 1-9 then 0 cover the first ten reasons; the rest are mouse-only.
const reasonShortcut = (index: number): string | null => {
  if (index < 9) return (index + 1).toString();
  if (index === 9) return "0";
  return null;
};

const buildSchema = (reasonGroup: CandidacyRejectionReasonGroup) =>
  z
    .object({
      date: nullableDateSchema,
      note: z.string().optional(),
      reason: z
        .nativeEnum(CandidacyRejectionReason)
        .nullable()
        .refine((value) => value !== null, {
          message: "Select a rejection reason.",
        }),
    })
    .superRefine((data, ctx) => {
      if (
        data.reason === CandidacyRejectionReason.OTHER &&
        !data.note?.trim()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Add a note when selecting Other.",
          path: ["note"],
        });
      }
      if (
        reasonGroup === CandidacyRejectionReasonGroup.POST_OFFER &&
        !data.date
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "A date is required.",
          path: ["date"],
        });
      }
    });

type CandidacyRejectFormValues = z.input<ReturnType<typeof buildSchema>>;

export const CandidacyRejectForm = ({
  mixpanelScreen,
  onConfirm,
  reasonGroup,
}: {
  mixpanelScreen: MIXPANEL_SCREENS;
  onConfirm: (payload: {
    date?: string;
    note?: string;
    reason: CandidacyRejectionReason;
  }) => void;
  reasonGroup: CandidacyRejectionReasonGroup;
}) => {
  /* React Hooks */
  const form = useForm<CandidacyRejectFormValues>({
    defaultValues: { date: null, note: "", reason: null },
    resolver: zodResolver(buildSchema(reasonGroup)),
  });
  const selectedReason = form.watch("reason");
  const reasons = candidacyRejectionReasonGroupToReasons[reasonGroup];

  const selectedPlacementStatus =
    selectedReason && reasonGroup === CandidacyRejectionReasonGroup.POST_OFFER
      ? // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
        candidacyRejectionReasonToPlacementStatus[selectedReason] ||
        PlacementStatus.REJECTED
      : undefined;

  // Digit selects a reason, Enter submits (Cmd+Enter from inside the note,
  // where plain Enter makes a newline).
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey && event.key === "Enter") {
        event.preventDefault();
        mixpanelUtils.trackKeyboardShortcutUsed(
          "Cmd+Enter (Confirm Reject)",
          mixpanelScreen,
        );
        void form.handleSubmit(handleSubmit)();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const isTyping =
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLInputElement;

      // Digits 1-9 then 0 select the first ten reasons; any past that are
      // mouse-only.
      if (!isTyping && /^[0-9]$/.test(event.key)) {
        const index = event.key === "0" ? 9 : Number(event.key) - 1;
        if (index < reasons.length) {
          event.preventDefault();
          mixpanelUtils.trackKeyboardShortcutUsed(
            `${event.key} (Rejection reason)`,
            mixpanelScreen,
          );
          form.setValue("reason", reasons[index], { shouldValidate: true });
          return;
        }
      }

      if (event.key !== "Enter" || event.shiftKey || isTyping) return;
      if (form.getValues("reason") == null) return;

      event.preventDefault();
      mixpanelUtils.trackKeyboardShortcutUsed(
        "Enter (Confirm Reject)",
        mixpanelScreen,
      );
      void form.handleSubmit(handleSubmit)();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, mixpanelScreen, reasons]);

  // The note is required for Other, so jump straight into it on select.
  useEffect(() => {
    if (selectedReason !== CandidacyRejectionReason.OTHER) return;
    form.setFocus("note");
  }, [form, selectedReason]);

  /* Handlers */
  const handleSubmit = (data: CandidacyRejectFormValues) => {
    if (data.reason == null) return;

    mixpanelUtils.trackFormSubmission("Confirm Reject", mixpanelScreen);
    onConfirm({
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      date: data.date || undefined,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      note: data.note?.trim() || undefined,
      reason: data.reason,
    });
    form.reset();
  };

  /* Render */
  const renderReasonCard = (
    reason: CandidacyRejectionReason,
    index: number,
  ) => {
    const isSelected = selectedReason === reason;
    const shortcut = reasonShortcut(index);

    return (
      <Button
        className={cn(
          "h-auto w-full flex-col items-start gap-1 rounded-lg border px-3 py-2.5 text-left shadow-none",
          isSelected
            ? "border-primary-600 bg-primary-100 text-slate-900 hover:border-primary-600 hover:bg-primary-100 hover:text-slate-900"
            : "border-slate-200 bg-white text-slate-900 hover:border-slate-300 hover:bg-slate-50",
        )}
        key={reason}
        onClick={() => {
          form.setValue("reason", reason, { shouldValidate: true });
        }}
        type="button"
        variant="outlineSecondary"
      >
        <Column className="w-full min-w-0 gap-0.5">
          <Row className="items-center gap-2">
            <Text
              className={cn(
                "text-sm leading-snug",
                isSelected ? "font-semibold text-primary-900" : "font-medium",
              )}
            >
              {candidacyRejectionReasonToTitle[reason]}
            </Text>
            {shortcut ? (
              <kbd className="pointer-events-none inline-flex h-5 min-w-[20px] items-center justify-center rounded bg-slate-100 px-1 font-mono text-[10px] font-semibold text-slate-600">
                {shortcut}
              </kbd>
            ) : null}
          </Row>
          <Text
            className={cn(
              "text-xs font-normal leading-snug",
              isSelected ? "text-primary-800/80" : "text-slate-500",
            )}
          >
            {candidacyRejectionReasonToSubtitle[reason]}
          </Text>
        </Column>
      </Button>
    );
  };

  const renderReasonCards = () => {
    return (
      <FormField
        control={form.control}
        name="reason"
        render={() => (
          <FormItem className="flex min-h-0 flex-1 flex-col text-slate-700">
            <FormLabel>Rejection reason</FormLabel>
            <Column className="min-h-0 flex-1 gap-2 overflow-y-auto pr-0.5">
              {reasons.map(renderReasonCard)}
            </Column>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  };

  const render = () => {
    return (
      <Form {...form}>
        <form
          className="flex min-h-0 flex-1 flex-col gap-3 text-sm"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          {renderReasonCards()}
          {selectedPlacementStatus ? (
            <FormDateInput
              fieldName="date"
              form={form}
              label={
                selectedPlacementStatus === PlacementStatus.REJECTED
                  ? "Date declined"
                  : "Date revoked"
              }
            />
          ) : null}
          {selectedReason ? (
            <FormTextAreaInput
              fieldName="note"
              form={form}
              inputClassName="min-h-[72px] resize-none"
              label="Note"
              optional={selectedReason !== CandidacyRejectionReason.OTHER}
              placeholder={
                candidacyRejectionReasonGroupToNotePlaceholder[reasonGroup]
              }
            />
          ) : null}
          <Row className="pt-1">
            <Button
              className="h-12 w-full gap-2 rounded-xl border-red-500 bg-red-50 px-4 text-sm font-semibold text-red-600 hover:border-red-500 hover:bg-red-50 hover:text-red-600"
              disabled={selectedReason == null}
              type="submit"
              variant="outlineDestructive"
            >
              Confirm Reject
              <KeyboardShortcut text="↵" variant="red" />
            </Button>
          </Row>
        </form>
      </Form>
    );
  };

  return render();
};
